import React, { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useBodySummary } from '../../hooks/useWearables';
import {
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  Button,
  Skeleton,
  Select,
  TextInput,
  Alert,
} from '@mantine/core';
import { IconScale, IconRefresh } from '@tabler/icons-react';

const RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

function getDateRange(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - Number(days));
  return { start_date: start.toISOString().split('T')[0], end_date: end.toISOString().split('T')[0] };
}

function Body() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [rangeDays, setRangeDays] = useState('7');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [cursor, setCursor] = useState(null);

  const selectedRange = rangeDays === 'custom'
    ? (
      customStart
        ? { start_date: customStart, end_date: customEnd || new Date().toISOString().split('T')[0] }
        : getDateRange(7)
    )
    : getDateRange(rangeDays);

  const { summaries, pagination, loading, error, refresh } = useBodySummary({
    limit: 50,
    startDate: selectedRange.start_date,
    endDate: selectedRange.end_date,
    cursor,
  });

  const latestWeight = summaries.length > 0 ? summaries.find((s) => s.weight_kg != null)?.weight_kg : null;
  const weights = summaries.filter((s) => s.weight_kg != null).map((s) => s.weight_kg);
  const avgWeight = weights.length > 0 ? weights.reduce((a, b) => a + b, 0) / weights.length : null;
  const bodyFatValues = summaries.filter((s) => s.body_fat_percent != null).map((s) => s.body_fat_percent);
  const avgBodyFat = bodyFatValues.length > 0 ? bodyFatValues.reduce((a, b) => a + b, 0) / bodyFatValues.length : null;

  const handleRangeChange = (value) => {
    const nextRange = value || '7';
    setCursor(null);
    setRangeDays(nextRange);
    if (nextRange !== 'custom') {
      refresh({ ...getDateRange(nextRange), limit: 50, cursor: null });
    }
  };

  if (authLoading) return (<Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400}><Skeleton height={400} /></Container>);
  if (!isAuthenticated) return (<Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400}><Card withBorder><Title order={2}>Body</Title><Text c="dimmed" mt="xs">Please log in.</Text></Card></Container>);

  return (
    <Container size="xl" py={{ base: 'lg', sm: 'xl' }} maw={1400} style={{ marginTop: 0 }}>
      <Stack gap="md">
        <Title order={1} size="h2" style={{ marginTop: 0 }}>Body</Title>
        {error && <Alert color="red" variant="light">{error}</Alert>}

        <Card withBorder radius="md" p="md">
          <Group justify="space-between" mb="md">
            <Text fw={600}>Body Metrics</Text>
            <Group>
              <Select data={RANGE_OPTIONS} value={rangeDays} onChange={handleRangeChange} w={140} />
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={() => {
                  setCursor(null);
                  refresh({ ...selectedRange, limit: 50, cursor: null });
                }}
                disabled={loading}
              >
                Refresh
              </Button>
            </Group>
          </Group>
          {rangeDays === 'custom' && (
            <Group mb="md">
              <TextInput
                label="Start date"
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
              <TextInput
                label="End date"
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
              <Button
                mt={24}
                variant="light"
                size="sm"
                onClick={() => {
                  setCursor(null);
                  refresh({ ...selectedRange, limit: 50, cursor: null });
                }}
                disabled={!customStart || loading}
              >
                Load Data
              </Button>
            </Group>
          )}

          <Grid mb="md">
            <Grid.Col span={4}><Text size="sm" c="dimmed">Latest Weight</Text><Text size="lg" fw={700}>{latestWeight != null ? `${latestWeight} kg` : '-'}</Text></Grid.Col>
            <Grid.Col span={4}><Text size="sm" c="dimmed">Avg Weight</Text><Text size="lg" fw={700}>{avgWeight != null ? `${avgWeight.toFixed(1)} kg` : '-'}</Text></Grid.Col>
            <Grid.Col span={4}><Text size="sm" c="dimmed">Avg Body Fat</Text><Text size="lg" fw={700}>{avgBodyFat != null ? `${avgBodyFat.toFixed(1)}%` : '-'}</Text></Grid.Col>
          </Grid>

          {loading ? (
            <Stack gap="xs">{[1,2,3,4,5].map((i) => <Skeleton key={i} height={80} />)}</Stack>
          ) : summaries.length > 0 ? (
            <Stack gap="md">
              {summaries.map((s, idx) => (
                <Card key={idx} withBorder radius="md" p="md">
                  <Group justify="space-between">
                    <div>
                      <Text fw={600}>{s.date || s.start_date || '-'}</Text>
                      <Text size="xs" c="dimmed">Body summary</Text>
                    </div>
                    <Group gap="md">
                      {s.weight_kg != null && <Group gap="xs"><IconScale size={16} /><Text size="lg" fw={600}>{s.weight_kg} kg</Text></Group>}
                      {s.body_fat_percent != null && <Text size="sm">Body fat: {s.body_fat_percent.toFixed(1)}%</Text>}
                      {s.bmi != null && <Text size="sm">BMI: {s.bmi.toFixed(1)}</Text>}
                    </Group>
                  </Group>
                  {(s.muscle_mass_kg != null || s.basal_body_temperature_celsius != null || (s.blood_pressure && (s.blood_pressure.systolic_mmhg != null || s.blood_pressure.diastolic_mmhg != null))) && (
                    <Text size="xs" c="dimmed" mt="xs">
                      {s.muscle_mass_kg != null && `Muscle: ${s.muscle_mass_kg} kg`}
                      {s.blood_pressure && (s.blood_pressure.systolic_mmhg != null || s.blood_pressure.diastolic_mmhg != null) && ` • BP: ${s.blood_pressure.systolic_mmhg || '-'}/${s.blood_pressure.diastolic_mmhg || '-'}`}
                      {s.basal_body_temperature_celsius != null && ` • Temp: ${s.basal_body_temperature_celsius}°C`}
                    </Text>
                  )}
                </Card>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" ta="center" py="xl">No body metrics available</Text>
          )}
          {(pagination?.previous_cursor || pagination?.has_more) && (
            <Group justify="space-between" mt="md">
              <Button
                variant="light"
                disabled={!pagination?.previous_cursor || loading}
                onClick={() => {
                  setCursor(pagination.previous_cursor);
                  refresh({ ...selectedRange, limit: 50, cursor: pagination.previous_cursor });
                }}
              >
                Previous Page
              </Button>
              <Text size="sm" c="dimmed">
                {pagination?.total_count != null ? `${pagination.total_count} total` : 'Paginated results'}
              </Text>
              <Button
                variant="light"
                disabled={!pagination?.has_more || loading}
                onClick={() => {
                  setCursor(pagination.next_cursor);
                  refresh({ ...selectedRange, limit: 50, cursor: pagination.next_cursor });
                }}
              >
                Next Page
              </Button>
            </Group>
          )}
        </Card>
      </Stack>
    </Container>
  );
}

export default Body;
